import React, { useState, useEffect, useRef, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import { disableTyping, getDeviceType, isSafari, isOutsideRegularHours } from 'utils';
import TimepickerUIHoursInput from './TimepickerUIHoursInput';
import DatePickerHoursInput from './DatePickerHoursInput';
import ConfirmationModal from 'components/Modal/ConfirmationModal';
import './timePicker.scss';

const formatTime = time => time.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

const TimeInput = ({
    names,
    values,
    disabled = { date: false, startTime: false, endTime: false },
    index,
    setFieldValue,
    setFieldTouched,
    isNew = true,
}) => {
    const [isDate, setIsDate] = useState(false);
    const [shouldOpenStartTime, setShouldOpenStartTime] = useState(false);
    const [deviceType] = useState(getDeviceType());
    const [pendingConfirmation, setPendingConfirmation] = useState(null);

    const eventDateMinDate = new Date();
    const eventDateMaxDate = new Date().setDate(eventDateMinDate.getDate() + 120);
    const showDateUnset = !isDate && isNew;

    const dateRef = useRef(null);

    useEffect(() => {
        // for hiding keyboard on mobile devices
        // see https://stackoverflow.com/questions/48805053/hide-native-keyboard-on-mobile-when-using-react-date-picker
        if (deviceType === 'mobile' && dateRef.current !== null) {
            dateRef.current.input.readOnly = true;
        }
    }, []);

    const onTimeSelected = useCallback(
        (time, fieldName, label, extraUpdates = [], onRevert = null) => {
            if (isOutsideRegularHours(time)) {
                setPendingConfirmation({ time, fieldName, label, extraUpdates, onRevert });
            } else {
                setFieldValue(fieldName, time);
                extraUpdates.forEach(u => setFieldValue(u.fieldName, u.time));
            }
        },
        [setFieldValue],
    );

    const handleConfirm = () => {
        setFieldValue(pendingConfirmation.fieldName, pendingConfirmation.time);
        pendingConfirmation.extraUpdates.forEach(u => setFieldValue(u.fieldName, u.time));
        setPendingConfirmation(null);
    };

    const handleGoBack = () => {
        pendingConfirmation?.onRevert?.();
        setPendingConfirmation(null);
    };

    const pendingMessage = pendingConfirmation
        ? `Just checking! You selected ${formatTime(pendingConfirmation.time)} in the middle of the night. Please confirm this is the correct ${pendingConfirmation.label}.`
        : '';

    return (
        <>
            <div className="field-container">
                <div className="time-container-row spacer">
                    <span className="placeholder">{'Date *'}</span>
                    <DatePicker
                        ref={dateRef}
                        selected={values.date}
                        onChange={value => {
                            setFieldValue(names.date, value);
                            setIsDate(true);
                            setShouldOpenStartTime(true);
                        }}
                        onChangeRaw={disableTyping}
                        onBlur={() => {
                            setFieldTouched(`streams[${index}].date`, true, false);
                            setIsDate(true);
                        }}
                        dateFormat="MM/dd/yyyy"
                        minDate={eventDateMinDate}
                        maxDate={eventDateMaxDate}
                        disabled={disabled.date}
                        className={`${showDateUnset ? ' field-unset' : ''}`}
                        required
                    />
                </div>
                {/* The below solution is not ideal,
                 * TODO: figure out how to get timepicker-ui to work on safari mobile/tablet
                 */}
                {isSafari() && deviceType !== 'desktop' ? (
                    <DatePickerHoursInput
                        names={names}
                        values={values}
                        disabled={disabled}
                        index={index}
                        setFieldTouched={setFieldTouched}
                        isNew={isNew}
                        shouldOpenStartTime={shouldOpenStartTime}
                        onStartOpened={() => setShouldOpenStartTime(false)}
                        onTimeSelected={onTimeSelected}
                    />
                ) : (
                    <TimepickerUIHoursInput
                        names={names}
                        values={values}
                        disabled={disabled}
                        index={index}
                        isNew={isNew}
                        shouldOpenStartTime={shouldOpenStartTime}
                        onStartOpened={() => setShouldOpenStartTime(false)}
                        onTimeSelected={onTimeSelected}
                    />
                )}
            </div>
            <ConfirmationModal
                isOpen={!!pendingConfirmation}
                message={pendingMessage}
                onConfirm={handleConfirm}
                onGoBack={handleGoBack}
            />
        </>
    );
};

export default TimeInput;
